import { Injectable } from '@angular/core';

import { GuardFinderError } from '../../../error';
import { UnitResult } from '../../../error/Domain/unit-result';
import { HttpErrorResponse } from './http-error-response';

/**
 * Type definition for Laravel API error responses.
 */
export type LaravelApiError = {
    success: boolean;
    message: string;
    errors?: { [key: string]: string[] } | string;
};

/**
 * Service for mapping Laravel API errors to UnitResult objects.
 *
 * Implements the ErrorMapper interface to provide specific handling
 * for Laravel API error formats. Extracts error messages and converts
 * them to standardized Error objects within UnitResult containers.
 */
@Injectable({
    providedIn: 'root'
})
export class LaravelErrorMapperService {
    /**
     * Maps a Laravel API error response to a UnitResult failure object.
     *
     * @param error - The HTTP error response from the Laravel API.
     * @returns A UnitResult failure object with appropriate Error instances.
     */
    mapError<T>(error: HttpErrorResponse): UnitResult<T> {
        const errors: GuardFinderError[] = [];

        // Handle network errors or missing error data
        if (!error.error || !error.status) {
            return UnitResult.failure<T>([new GuardFinderError('Network error or server unavailable')]);
        }

        const errorResponse: LaravelApiError = error.error as LaravelApiError;

        // Handle rate limiting (429 Too Many Attempts) specifically
        if (error.status === 429) {
            return UnitResult.failure<T>([
                new GuardFinderError(
                    errorResponse.message || 'Too many requests. Please try again later.',
                    'RATE_LIMIT_EXCEEDED'
                )
            ]);
        }

        // Extract error messages from the errors object or use the direct message
        console.log(errorResponse);
        if (errorResponse?.errors) {
            if (typeof errorResponse.errors === 'string') {
                errors.push(new GuardFinderError(errorResponse.errors, 'VALIDATION_ERROR'));
            } else {
                const errorKeys = Object.keys(errorResponse.errors);
                for (const key of errorKeys) {
                    const messages = errorResponse.errors[key];
                    if (messages?.length > 0) {
                        messages.forEach(message => {
                            errors.push(new GuardFinderError(message, 'VALIDATION_ERROR', { field: key }));
                        });
                    }
                }
            }
        }

        // If no specific errors were found, use the general message
        if (errors.length === 0) {
            return UnitResult.failure<T>([
                new GuardFinderError(errorResponse.message || 'Failed to send data', 'API_ERROR')
            ]);
        }

        return UnitResult.failure<T>(errors);
    }
}
